import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Image,
  StyleSheet,
  Alert,
  ScrollView,
  SafeAreaView,
  Animated,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { NavigationProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getPendingInvitationToken, routeToPendingInvitationIfAny } from '../../navigation/pendingInvitation';
import OrnamentBackground from "../../components/OrnamentBackground";
import { API_URL } from '../../config';
import deviceTrustService from '../../services/deviceTrustService';
import twoFactorService from '../../services/twoFactorService';
import { authLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';
import { analytics, AnalyticsEvent } from '../../services/analytics';
import { pickPostAuthTarget, wizardScreenForStep, letterScreenForStep } from '../../navigation/postAuthRoute';
import { shouldRouteToUnlockAtSignIn } from '../../navigation/postAuthUnlockRoute';
import {
  getDeviceTrustToken,
  removeDeviceTrustToken,
  setAuthToken,
  setRefreshToken,
} from '../../services/auth/tokenStorage';
import {
  checkRateLimit,
  RateLimitStatus,
  recordFailure,
  recordSuccess,
} from '../../services/auth/rateLimiter';
import RateLimitBanner from '../../components/RateLimitBanner';

// Eye icons for password visibility toggle
import eyeIcon from '../../assets/icons/Eye.png';
import eyeClosedIcon from '../../assets/icons/Eye-closed.png';

const LoginScreen = ({ navigation }: { navigation: NavigationProp<any> }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginError, setShowLoginError] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimit, setRateLimit] = useState<RateLimitStatus | null>(null);
  const slideAnim = useRef(new Animated.Value(-50)).current;

  useEffect(() => {
    Animated.timing(slideAnim, {
      toValue: showLoginError ? 0 : -50,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [showLoginError]);

  // Re-check the persisted rate-limit state on mount so a user who killed
  // the app during a lockout sees the banner again on re-entry.
  useEffect(() => {
    checkRateLimit('login').then((status) => {
      if (!status.allowed) setRateLimit(status);
    }).catch(() => { /* best-effort — do not block the UI */ });
  }, []);

  const performLogin = async (loginEmail: string, loginPassword: string) => {
    setIsLoading(true);
    setShowLoginError(false);
    analytics.track(AnalyticsEvent.LoginStarted);

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginEmail.toLowerCase().trim(),
          password: loginPassword,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        authLogger.debug('Login credentials valid, checking TOTP status...');
        // Password was correct — reset the login-attempts counter. A wrong
        // TOTP code downstream accrues on its own counter ('totp').
        try {
          await recordSuccess('login');
          setRateLimit(null);
        } catch (rlErr) {
          authLogger.error('recordSuccess(login) failed:', rlErr);
        }

        // With TOTP or text codes on, the server answers the password with a
        // short-lived ticket instead of a session (2026-09-23); the code or
        // a trusted device, presented with that ticket, earns the session.
        // An older server without tickets is asked the old way.
        let totpData: { isTotpEnabled: boolean; requiresSms2fa?: boolean; userId: string };
        const secondFactorTicket: string | undefined = result.secondFactorTicket;
        if (result.requiresSecondFactor) {
          totpData = {
            isTotpEnabled: result.secondFactor === 'totp',
            requiresSms2fa: result.secondFactor === 'sms',
            userId: result.userId,
          };
        } else {
          const totpCheckResponse = await fetch(`${API_URL}/auth/check-totp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: loginEmail.toLowerCase().trim() }),
          });

          if (!totpCheckResponse.ok) {
            authLogger.error('TOTP check failed:', totpCheckResponse.status);
            setLoginErrorMessage('Failed to verify authentication status. Please try again.');
            setShowLoginError(true);
            setIsLoading(false);
            return;
          }

          totpData = await totpCheckResponse.json();
        }
        authLogger.debug('TOTP check result:', { isTotpEnabled: totpData.isTotpEnabled, userId: totpData.userId });

        if (totpData.isTotpEnabled || totpData.requiresSms2fa) {
          // Two-factor is on: a device remembered for 30 days skips the code
          // (authenticator or text codes alike), never the password.
          const deviceTrustToken = await getDeviceTrustToken();

          if (deviceTrustToken) {
            // Try to verify device trust
            authLogger.debug('Checking device trust token...');
            try {
              const trustResponse = await fetch(`${API_URL}/auth/verify-device-trust`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  userId: totpData.userId,
                  deviceTrustToken,
                  secondFactorTicket,
                }),
              });

              const trustData = await trustResponse.json();

              if (trustData.success && trustData.isTrusted) {
                // Device is trusted - skip TOTP verification
                authLogger.debug('Device trusted, skipping TOTP verification');

                // Store the new tokens in the keychain
                await setAuthToken(trustData.access_token);
                if (trustData.refresh_token) {
                  await setRefreshToken(trustData.refresh_token);
                }
                await AsyncStorage.setItem('@user_id', trustData.user.id);
                await AsyncStorage.setItem('@user_email', trustData.user.email);

                analytics.identify(trustData.user.id);
                analytics.track(AnalyticsEvent.LoginSucceeded, { method: 'device_trust' });

                // Register device for push notifications
                try {
                  const { pushNotificationService } = require('../../services/pushNotificationService');
                  await pushNotificationService.getAndRegisterToken();
                } catch (e) {
                  console.log('[Push] Token registration after login failed (non-fatal):', e);
                }

                // Store account setup flags for session persistence
                // These ensure user is taken to Login (not SignUp) when token expires
                await AsyncStorage.setItem('@email_verified', 'true');
                await AsyncStorage.setItem('@2fa_setup_complete', 'true');

                setIsLoading(false);

                // An invitation opened before sign-in comes first.
                if (await routeToPendingInvitationIfAny(navigation)) return;

                // Navigate based on onboarding status (new wizard flow takes precedence)
                const target = pickPostAuthTarget(trustData.user, {
                  legacyOnboardingCompleted: !!trustData.isOnboardingCompleted,
                });
                if (target.kind === 'letterFlow') {
                  navigation.reset({
                    index: 0,
                    routes: [{
                      name: 'LetterFirst',
                      state: { routes: [{ name: letterScreenForStep(target.initialStep) }] },
                    }],
                  });
                } else if (target.kind === 'wizard') {
                  navigation.reset({
                    index: 0,
                    routes: [{
                      name: 'OnboardingWizard',
                      state: { routes: [{ name: wizardScreenForStep(target.initialStep) }] },
                    }],
                  });
                } else if (target.kind === 'dashboard') {
                  // Gate: if the user has enrolled a recovery code but the
                  // unwrapped key isn't cached on this device, route to
                  // UnlockVault before Dashboard.
                  if (await shouldRouteToUnlockAtSignIn()) {
                    navigation.reset({
                      index: 0,
                      routes: [{ name: 'Auth', state: { routes: [{ name: 'UnlockVault' }] } }],
                    });
                  } else {
                    navigation.reset({
                      index: 0,
                      routes: [{ name: 'Dashboard' }],
                    });
                  }
                } else {
                  navigation.reset({
                    index: 0,
                    routes: [{ name: 'Auth', state: { routes: [{ name: 'Splash' }] } }],
                  });
                }
                return;
              } else {
                // Not trusted for this account. The token is kept: on a
                // shared phone it may be another account's, and the server
                // only honours it for the account it was issued to.
                authLogger.debug('Device not trusted for this account');
              }
            } catch (error) {
              authLogger.error('Device trust verification error:', error);
            }
          }

          // Not remembered: the code step, with the password's ticket. TOTP
          // wins when both are on; text codes otherwise (Phase 8).
          setIsLoading(false);
          navigation.navigate(totpData.isTotpEnabled ? 'TotpVerification' : 'SmsVerification', {
            userId: totpData.userId,
            email: loginEmail.toLowerCase().trim(),
            secondFactorTicket,
          });
          return;
        }

        // If TOTP is not enabled, user needs to set it up (required for all users)
        // Store token now since they'll need it for TOTP setup
        authLogger.debug('User needs to set up TOTP, storing token for setup');
        await setAuthToken(result.access_token);
        await AsyncStorage.setItem('user', JSON.stringify(result.user));
        if (result.user?.id) analytics.identify(String(result.user.id));
        analytics.track(AnalyticsEvent.LoginSucceeded, { method: 'password_needs_totp' });
        await AsyncStorage.setItem('hasLoggedIn', 'true');
        await AsyncStorage.setItem('@email_verified', 'true'); // User can only login if email is verified

        // Register device for push notifications
        try {
          const { pushNotificationService } = require('../../services/pushNotificationService');
          await pushNotificationService.getAndRegisterToken();
        } catch (e) {
          console.log('[Push] Token registration after login failed (non-fatal):', e);
        }

        setIsLoading(false);

        // An invitation opened before sign-in comes first. An enrolled
        // account whose key isn't on this device still unlocks before it;
        // the unlock screen hands on to the invitation.
        if (await getPendingInvitationToken()) {
          if (await shouldRouteToUnlockAtSignIn()) {
            navigation.reset({
              index: 0,
              routes: [{ name: 'Auth', state: { routes: [{ name: 'UnlockVault' }] } }],
            });
          } else {
            await routeToPendingInvitationIfAny(navigation);
          }
          return;
        }

        // Letter-first users (onboardingVersion 2) are never funneled into
        // security setup at login — TOTP/SMS is offered after upgrade and
        // stays available in Settings. Resume their letter flow (or land
        // on Dashboard if they've completed onboarding) instead.
        if ((result.user?.onboardingVersion ?? 0) >= 2) {
          const letterTarget = pickPostAuthTarget(result.user, {
            legacyOnboardingCompleted: !!result.isOnboardingCompleted,
          });
          if (letterTarget.kind === 'letterFlow') {
            navigation.reset({
              index: 0,
              routes: [{
                name: 'LetterFirst',
                state: { routes: [{ name: letterScreenForStep(letterTarget.initialStep) }] },
              }],
            });
          } else if (await shouldRouteToUnlockAtSignIn()) {
            // Same gate as every other path into Dashboard: an enrolled
            // account whose key isn't on this device unlocks first.
            navigation.reset({
              index: 0,
              routes: [{ name: 'Auth', state: { routes: [{ name: 'UnlockVault' }] } }],
            });
          } else {
            navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] });
          }
          return;
        }

        // Two-factor is offered after an upgrade and lives in Settings;
        // sign-in never detours through the Security Chooser. Same unlock
        // gate as every other path into Dashboard.
        if (await shouldRouteToUnlockAtSignIn()) {
          navigation.reset({
            index: 0,
            routes: [{ name: 'Auth', state: { routes: [{ name: 'UnlockVault' }] } }],
          });
        } else {
          navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] });
        }
        return;
      } else {
        // Handle login error
        setLoginErrorMessage(result.message || 'Invalid email or password');
        setShowLoginError(true);
        analytics.track(AnalyticsEvent.LoginFailed, { reason: result.message || 'invalid_credentials' });
        // Record the failure — server-rejected credentials is what the
        // rate-limit is here to discourage. Network errors (catch block
        // below) are NOT counted.
        try {
          const status = await recordFailure('login');
          setRateLimit(status.allowed ? null : status);
        } catch (rlErr) {
          authLogger.error('recordFailure(login) failed:', rlErr);
        }
      }
    } catch (error) {
      authLogger.error('Login error:', error);
      setLoginErrorMessage('Network error. Please check your connection and try again.');
      setShowLoginError(true);
      analytics.track(AnalyticsEvent.LoginFailed, { reason: 'network_error' });
      // Deliberately no recordFailure here — a flaky network shouldn't
      // accrue toward a lockout.
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      Alert.alert("Error", "Please enter both email and password.");
      return;
    }

    // Pre-flight rate-limit check — the persisted counter survives app
    // kills, so a user can't reset by force-quitting during a lockout.
    const status = await checkRateLimit('login');
    if (!status.allowed) {
      setRateLimit(status);
      return;
    }

    await performLogin(email, password);
  };

  const handleForgotPassword = () => {
    navigation.navigate('ForgotPassword');
  };

  const handleSignUp = () => {
    navigation.navigate("SignUp");
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          style={styles.fullScrollView}
          contentContainerStyle={styles.fullScrollContent}
          keyboardShouldPersistTaps="handled"
          bounces={false}
        >
          <View style={styles.topSection}>
            <Image source={require('../../assets/logo.png')} style={styles.logo} />
            <Text style={styles.welcomeText}>Welcome Back!</Text>
            <Text style={styles.description}>
              Sign in to access and update your personal details to organize your
              financial accounts, healthcare directives, estate plans, and personal
              wishes.
            </Text>
          </View>

          <View style={styles.mainContainer}>
        {showLoginError && (
          <Animated.View 
            style={[
              styles.errorBox,
              {
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            <View style={styles.errorContent}>
              <Image
                source={require('../../assets/icons/warning.png')}
                style={styles.warningIcon}
              />
              <Text style={styles.errorMessage}>
                {loginErrorMessage}
              </Text>
              <TouchableOpacity
                onPress={() => setShowLoginError(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>×</Text>
              </TouchableOpacity>
            </View>
          </Animated.View>
        )}

        <View style={styles.inputContainer}>
          <View style={styles.scrollView}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Email</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter your email address"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                placeholderTextColor="#A9A9A9"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Password</Text>
              <View style={styles.passwordInputContainer}>
                <TextInput
                  style={styles.passwordInput}
                  placeholder="Enter your password"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  placeholderTextColor="#A9A9A9"
                />
                <TouchableOpacity
                  style={styles.eyeButton}
                  onPress={() => setShowPassword(!showPassword)}
                >
                  <Image
                    source={showPassword ? eyeIcon : eyeClosedIcon}
                    style={styles.eyeIcon}
                  />
                </TouchableOpacity>
              </View>
              <TouchableOpacity
                onPress={handleForgotPassword}
                style={styles.forgotPasswordContainer}
              >
                <Text style={styles.forgotPasswordText}>Forgot Password?</Text>
              </TouchableOpacity>
            </View>

            {rateLimit && !rateLimit.allowed ? (
              <RateLimitBanner
                remainingMs={rateLimit.remainingMs}
                count={rateLimit.count}
                onExpired={() => setRateLimit(null)}
              />
            ) : null}

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[
                  styles.loginButton,
                  (isLoading || (rateLimit && !rateLimit.allowed)) &&
                    styles.loginButtonDisabled,
                ]}
                onPress={handleLogin}
                disabled={isLoading || !!(rateLimit && !rateLimit.allowed)}
              >
                <Text style={styles.loginButtonText}>
                  {isLoading ? 'Signing In...' : 'Login'}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.signUpContainer}>
              <Text style={styles.signUpText}>
                Don't have an account yet?{' '}
                <Text style={styles.signUpLink} onPress={handleSignUp}>
                  Sign Up
                </Text>
              </Text>
            </View>

            <View style={styles.bottomPadding} />
          </View>
        </View>
      </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  welcomeText: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: ms(20),
    paddingHorizontal: ms(40),
  },
  keyboardAvoid: {
    flex: 1,
  },
  fullScrollView: {
    flex: 1,
  },
  fullScrollContent: {
    flexGrow: 1,
  },
  mainContainer: {
    flex: 1,
    position: 'relative',
  },
  errorBox: {
    position: 'absolute',
    top: ms(-80),
    left: 0,
    right: 0,
    backgroundColor: '#E85C5C',
    paddingVertical: ms(25),
    paddingHorizontal: ms(20),
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    zIndex: 0,
    marginBottom: ms(-10),
  },
  inputContainer: {
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(30),
    position: 'relative',
    zIndex: 2,
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: 1,
    borderColor: '#d8dbdd',
  },
  passwordInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    borderWidth: 1,
    borderColor: '#d8dbdd',
  },
  passwordInput: {
    flex: 1,
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  eyeButton: {
    padding: ms(15),
  },
  eyeIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#6B7280',
  },
  forgotPasswordContainer: {
    alignItems: 'flex-end',
    marginTop: ms(8),
  },
  forgotPasswordText: {
    color: '#29BFC0',
    fontSize: ms(14, 0.3),
    fontWeight: '500',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  loginButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  loginButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loginButtonDisabled: {
    backgroundColor: '#A9A9A9',
    opacity: 0.7,
  },
  signUpContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(10),
    marginBottom: ms(20),
  },
  signUpText: {
    color: '#62656b',
    fontSize: ms(16, 0.3),
  },
  signUpLink: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(40),
  },

  errorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  warningIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#FFFFFF',
  },
  errorMessage: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    marginHorizontal: ms(10),
  },
  closeButton: {
    padding: ms(5),
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
  },
});

export default LoginScreen;